import { ApplicationTypeKey } from "@hub/data/values/data-keys.enum";
import { DocumentAnchor } from "@hub/models/document/document-anchor";
import { CategoryMetadata } from "@hub/models/metadata/category-metadata";
import { HeadMetadata } from "@hub/models/metadata/head-metadata";
import { BannerConfig } from "@hub/models/ui/banner-config";
import { Breadcrumb } from "@hub/models/ui/breadcrumb";
import { CodeBlocksData } from "@hub/models/ui/code-blocks-data";
import { ContentImage } from "@hub/models/ui/content-image";
import { PageConfig } from "@hub/models/ui/page-config";
import { hubOriginUrl, hubSiteTree } from "@hub/services/config.service";
import { getBreadCrumbs } from "@hub/services/content/common/get-breadcrumbs";
import {
  extractCodeBlocks,
  extractContentHeadings,
  extractImageData,
  extractUiIncludesData,
  getDocumentUiConfig,
} from "@hub/services/content/common/process-content.utils";
import { GetStartedData } from "@hub/services/content/models/get-started-data";
import { MetadataService } from "@hub/services/metadata.service";
import { createUrlPath } from "@hub/services/utils/path.utils";
import { ReadyToSecureAd } from "@hub/ui/ads/ready-to-secure-ad";
import { Auth0Header } from "@hub/ui/auth0/auth0-header";
import { ContentBanner } from "@hub/ui/banners/content-banner";
import { Breadcrumbs } from "@hub/ui/breadcrumbs/breadcrumbs";
import { ShellLayout } from "@hub/ui/layouts/shell-layout";
import {
  customMarkdownComponents,
  renderMarkdownCodeBlock,
  renderMarkdownParagraph,
} from "@hub/ui/markdown/markdown.helper";
import { GetStaticPaths, GetStaticProps } from "next";
import { ParsedUrlQuery } from "querystring";
import React from "react";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import styles from "../guides/[type]/category.module.css";

interface TypePageProps {
  pageContent: string;
  pageConfig: PageConfig;
  bannerConfig: BannerConfig;
  pageSections: DocumentAnchor[];
  pageIncludeMetadata: { [index: string]: unknown };
  pageImages: {
    [index: string]: ContentImage;
  };
  pageCodeBlocks: CodeBlocksData;
  breadcrumbs?: Breadcrumb[];
  headMetadata?: HeadMetadata;
  type?: ApplicationTypeKey;
}

const TypePage: React.FC<TypePageProps> = ({
  pageContent,
  pageConfig,
  bannerConfig,
  pageIncludeMetadata,
  pageImages,
  pageCodeBlocks,
  breadcrumbs,
  headMetadata,
  type,
}) => {
  const pageTypesWithUpsell = new Set([
    ApplicationTypeKey.API,
    ApplicationTypeKey.WEB_APP,
    ApplicationTypeKey.MOBILE,
    ApplicationTypeKey.CLOUD,
    ApplicationTypeKey.SPA,
  ]);

  const withUpsell = type && pageTypesWithUpsell.has(type);

  return (
    <ShellLayout
      header={<Auth0Header />}
      pageConfig={pageConfig}
      headMetadata={headMetadata}
      breadcrumbs={
        breadcrumbs ? <Breadcrumbs breadcrumbs={breadcrumbs} /> : null
      }
      document={
        <div className={styles.page}>
          <ContentBanner {...bannerConfig} />
          <ReactMarkdown
            rehypePlugins={[rehypeRaw]}
            components={{
              ...customMarkdownComponents,
              p: (props: any) => renderMarkdownParagraph(props, pageImages),
              pre: (incomingProps: any) =>
                renderMarkdownCodeBlock(
                  incomingProps,
                  pageIncludeMetadata,
                  pageCodeBlocks,
                ),
            }}
          >
            {pageContent}
          </ReactMarkdown>
          {withUpsell && <ReadyToSecureAd />}
        </div>
      }
    />
  );
};

export default TypePage;

export const getStaticPaths: GetStaticPaths = async () => {
  const getStartedData = new GetStartedData();

  return {
    paths: getStartedData.allowed.applicationTypes.map((type) => {
      return createUrlPath([
        hubSiteTree.root.sections.getStarted.urlPath,
        type,
      ]);
    }),
    fallback: false,
  };
};
interface Params extends ParsedUrlQuery {
  type: string;
}

export const getStaticProps: GetStaticProps<TypePageProps, Params> = async (
  context,
) => {
  const { params } = context;

  if (!params) {
    return {
      notFound: true,
    };
  }

  const getStartedData = new GetStartedData();

  const document = getStartedData.findCategoryDocument(
    params.type as ApplicationTypeKey,
  );

  if (!document) {
    return {
      notFound: true,
    };
  }

  const { content, metadata } = document;

  // if (!isValidCategoryMetadata(metadata, document.path)) {
  //   throw new Error(
  //     `[Error /${params.category}/${params.type}]invalid category metadata`
  //   );
  // }

  const { framework, language, title, description, design, type } = metadata;

  const uiConfig = getDocumentUiConfig(metadata as CategoryMetadata);

  if (!uiConfig) {
    return { notFound: true };
  }

  const { pageConfig, bannerConfig } = uiConfig;

  let { content: optimalContent, includeMetadata } =
    await extractUiIncludesData(content);

  const { contentSectionHeadings: pageSections } = extractContentHeadings(
    optimalContent,
    "document",
  );
  const pageImages = await extractImageData(optimalContent);

  const { content: optimalContentWithCode, codeBlockData } =
    extractCodeBlocks(optimalContent);

  const frameworkDisplayName =
    MetadataService.getFrameworkDisplayName(framework);

  if (!frameworkDisplayName) {
    throw new Error(
      `[Error: /${params.type}/${params.category}] frameworkDisplayName undefined for ${framework}`,
    );
  }

  const programmingLanguageDisplayName =
    MetadataService.getProgrammingLanguageDisplayName(language);

  if (!programmingLanguageDisplayName) {
    throw new Error(
      `[Error: /${params.type}/${params.category}] programmingLanguageDisplayName undefined for ${language}`,
    );
  }

  const typeDisplayName = MetadataService.getApplicationTypeDisplayName(
    params.type as ApplicationTypeKey,
  );

  if (!typeDisplayName) {
    throw new Error(
      `[Error: /${params.type}/${params.category}] typeDisplayName undefined for ${params.type}`,
    );
  }

  const breadcrumbs = getBreadCrumbs({
    root: {
      label: hubSiteTree.root.displayName,
      path: hubSiteTree.root.urlPath,
    },
    sections: [
      {
        label: hubSiteTree.root.sections.getStarted.displayName,
        path: hubSiteTree.root.sections.getStarted.urlPath,
      },
      {
        label: typeDisplayName,
        path: params.type,
      },
    ],
  });

  pageConfig.canonicalUrl =
    hubOriginUrl +
    createUrlPath([
      hubSiteTree.root.urlPath,
      hubSiteTree.root.sections.getStarted.urlPath,
      params.type,
    ]);

  let headMetadataImage = `https://cdn.auth0.com/website/developer-center/preview-cards/resources_get-started.png`;

  if (design && design.illustration) {
    const { illustration } = design;

    headMetadataImage = illustration;
  }

  const headMetadata: HeadMetadata = {
    title: title,
    description: description,
    url: pageConfig.canonicalUrl,
    image: headMetadataImage,
    type: "website",
  };

  const appTypeIcon = MetadataService.getApplicationTypeIcon(
    params.type as ApplicationTypeKey,
  );

  if (!appTypeIcon) {
    return { notFound: true };
  }

  bannerConfig.icons = [];

  bannerConfig.icons.push({
    src: appTypeIcon,
    alt: `${params.type} icon`,
  });

  bannerConfig.icons.push({
    src: "/resources/logos/auth0-logo.svg",
    alt: `auth0 logo`,
  });

  const props: TypePageProps = {
    pageContent: optimalContentWithCode,
    pageConfig,
    bannerConfig,
    pageSections: pageSections,
    pageIncludeMetadata: includeMetadata,
    pageImages: pageImages,
    pageCodeBlocks: codeBlockData,
    breadcrumbs: breadcrumbs,
    headMetadata: headMetadata,
    type: type,
  };

  return {
    props,
  };
};
